import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { Check, ChevronsUpDown, Globe, Loader, Search } from 'lucide-react';
import * as React from 'react';
import { CircleFlag } from 'react-circle-flags';
import { useDebounce } from 'use-debounce';

interface MultiComboBoxOption {
  value?: string;
  label?: string;
  countryCode?: string;
  isRegion?: boolean;
  group?: string;
}

interface MultiComboBoxProps {
  fetchOptions: (query: string) => Promise<MultiComboBoxOption[]>;
  values: string[] | MultiComboBoxOption[];
  onChange: (values: string[] | MultiComboBoxOption[] | any) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  isEditing?: boolean;
  queryKey?: string;
  newParams?: string[];
  returnFullObject?: boolean;
  cypressName?: string;
  dataCy?: string;
  selectAllOptions?: boolean;
  onSelectAll?: () => void;
  isSelectAllActive?: boolean;
  maxHeight?: string | number;
  modal?: boolean;
  formatTriggerLabel?: (selected: MultiComboBoxOption[]) => string;
}

export function MultiComboBox({
  fetchOptions,
  values,
  onChange,
  helperText,
  placeholder = t('common:selectOptions'),
  disabled,
  label,
  isEditing,
  queryKey,
  newParams,
  returnFullObject = false,
  cypressName,
  dataCy,
  selectAllOptions,
  onSelectAll,
  isSelectAllActive,
  maxHeight = '200px',
  modal = true,
  formatTriggerLabel,
}: MultiComboBoxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const { data: options = [], isFetching } = useQuery({
    queryKey: [
      `multi-combo-options-${queryKey}`,
      debouncedSearchQuery,
      newParams,
    ],
    queryFn: () => fetchOptions(debouncedSearchQuery),
    staleTime: 5 * 60 * 1000,
    enabled: open || isEditing,
  });

  const [internalOptions, setInternalOptions] = React.useState<
    MultiComboBoxOption[]
  >([]);

  const newParamsKey = React.useMemo(
    () => JSON.stringify(newParams ?? []),
    [newParams],
  );

  React.useEffect(() => {
    setInternalOptions([]);
  }, [newParamsKey]);

  React.useEffect(() => {
    if (options.length > 0) {
      setInternalOptions((prev) => {
        const merged = [...prev];
        options.forEach((opt) => {
          if (!merged.some((o) => o.value === opt.value)) {
            merged.push(opt);
          }
        });
        return merged;
      });
    }
  }, [options]);

  const selectedValues = React.useMemo(() => {
    if (!Array.isArray(values)) return [];

    return values.map((val) => {
      if (typeof val === 'string') {
        const match = internalOptions.find((opt) => opt.value === val);
        return match || { value: val, label: val };
      }
      return val;
    });
  }, [values, internalOptions]);

  const toggleValue = (value: string | undefined) => {
    if (!value) return;

    const alreadySelected = selectedValues.some((v) => v.value === value);

    if (alreadySelected) {
      const updated = selectedValues.filter((v) => v.value !== value);
      onChange(returnFullObject ? updated : updated.map((v) => v.value!));
    } else {
      const selectedOption =
        options.find((opt) => opt.value === value) ||
        internalOptions.find((opt) => opt.value === value) || {
          value,
          label: value,
        };
      const updated = [...selectedValues, selectedOption];
      onChange(returnFullObject ? updated : updated.map((v) => v.value!));
    }
  };

  const displayValue = formatTriggerLabel
    ? formatTriggerLabel(selectedValues)
    : selectedValues.map((item) => item.label).join(', ');

  const renderCountryFlag = (option: MultiComboBoxOption) => {
    if (option.isRegion) {
      return (
        <div className='inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full'>
          <Globe className='h-4 w-4 text-primary' />
        </div>
      );
    }

    if (!option.countryCode) return null;

    return (
      <div className='inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full'>
        <CircleFlag countryCode={option.countryCode.toLowerCase()} height={20} />
      </div>
    );
  };

  const renderOptionLabel = (option: MultiComboBoxOption, emphasized = false) => (
    <>
      {renderCountryFlag(option)}
      <span
        className={cn(
          'flex-1 text-left break-words',
          emphasized && 'font-medium',
        )}
        title={option.label}
      >
        {option.label}
      </span>
    </>
  );

  const allOptionsSelected =
    options.length > 0 &&
    options.every((opt) => selectedValues.some((v) => v.value === opt.value));

  const allSelected = isSelectAllActive ?? allOptionsSelected;

  const toggleSelectAll = () => {
    if (onSelectAll) {
      onSelectAll();
      return;
    }

    if (allOptionsSelected) {
      onChange(returnFullObject ? [] : []);
    } else {
      onChange(returnFullObject ? options : options.map((opt) => opt.value));
    }
  };

  const [popoverWidth, setPopoverWidth] = React.useState<string>('auto');

  React.useEffect(() => {
    if (open && triggerRef.current) {
      const triggerWidth = triggerRef.current.offsetWidth;
      const viewportWidth = window.innerWidth;

      const idealWidth = Math.max(triggerWidth, 200);
      const maxAllowedWidth = viewportWidth * 0.9;

      setPopoverWidth(`${Math.min(idealWidth, maxAllowedWidth)}px`);
    }
  }, [open]);

  React.useEffect(() => {
    if (scrollRef.current && options.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollTop;
    }
  }, [options]);

  const selectedOptions = options.filter((option) =>
    selectedValues.some((v) => v.value === option.value)
  );
  const unselectedOptions = options.filter(
    (option) => !selectedValues.some((v) => v.value === option.value)
  );
  const hasGroups = options.some((option) => Boolean(option.group));
  const groupedOptions = React.useMemo(() => {
    if (!options.some((option) => Boolean(option.group))) {
      return [];
    }

    const groups: { name: string; items: MultiComboBoxOption[] }[] = [];

    options.forEach((option) => {
      const name = option.group ?? '';
      const existing = groups.find((group) => group.name === name);

      if (existing) {
        existing.items.push(option);
        return;
      }

      groups.push({ name, items: [option] });
    });

    return groups;
  }, [options]);

  const renderOptionRow = (option: MultiComboBoxOption, selected: boolean) => (
    <div
      key={`${selected ? 'selected' : 'option'}-${option.value}`}
      onClick={() => toggleValue(option.value)}
      className={cn(
        'relative flex cursor-pointer gap-2 select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'active:bg-accent active:text-accent-foreground',
        'min-h-[36px] flex-shrink-0',
        'sm:py-1.5 sm:min-h-[32px]',
        selected && 'bg-accent/20',
      )}
      role='option'
      aria-selected={selected}
    >
      <Check
        className={cn(
          'h-4 w-4 flex-shrink-0',
          selected ? 'opacity-100' : 'opacity-0',
        )}
      />
      {renderOptionLabel(option, selected)}
    </div>
  );

  return (
    <div className='flex flex-col gap-2 w-full'>
      {(label || selectAllOptions) && (
        <div className='flex items-center justify-between'>
          {label && <Label className='text-sm'>{label}</Label>}

          {selectAllOptions && (
            <span
              className='text-sm font-medium text-primary hover:text-primary/80 cursor-pointer'
              onClick={toggleSelectAll}
            >
              {allSelected ? t('common:deselect_all') : t('common:select_all')}
            </span>
          )}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant='outline'
            role='combobox'
            aria-expanded={open}
            data-cy={cypressName}
            disabled={disabled}
            className={`bg-[#09090b] line-clamp-1 flex justify-between disabled:bg-bgDisabled text-muted !text-sm ${
              helperText ? '!border-error' : 'border-colorBorder'
            }`}
          >
            <span className='truncate text-left flex-1 flex items-center gap-2 min-w-0'>
              {selectedValues.length === 1 &&
              (selectedValues[0].countryCode || selectedValues[0].isRegion) ? (
                <>
                  {renderCountryFlag(selectedValues[0])}
                  <span className='truncate'>
                    {selectedValues[0].label || placeholder}
                  </span>
                </>
              ) : (
                displayValue || placeholder
              )}
            </span>
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className='p-0'
          align='start'
          side='bottom'
          sideOffset={4}
          collisionPadding={10}
          style={{
            width: popoverWidth,
            minWidth: '200px',
            maxWidth: '90vw',
            transform: 'translateZ(0)',
            isolation: 'isolate',
          }}
        >
          <div className='flex flex-col border border-colorBorder rounded-md bg-popover text-popover-foreground'>
            <div className='flex items-center px-3 border-b border-colorBorder'>
              <Search className='mr-2 h-4 w-4 shrink-0 opacity-50' />
              <input
                placeholder={`${placeholder}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50'
              />
            </div>

            <div
              ref={scrollRef}
              className='scroll-container'
              style={{
                minHeight: 'auto',
                maxHeight:
                  typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
                isolation: 'isolate',
                contain: 'layout style',
                scrollBehavior: 'smooth',
                scrollbarWidth: 'thin',
                scrollbarColor: '#888 #f1f1f1',
              }}
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              {isFetching ? (
                <div className='h-full flex items-center justify-center py-8'>
                  <Loader className='w-4 h-4 animate-spin' />
                </div>
              ) : options.length === 0 ? (
                <div className='h-full flex items-center justify-center text-sm text-muted-foreground py-8'>
                  {t('common:emptyOption')}
                </div>
              ) : (
                <div className='p-1'>
                  {hasGroups
                    ? groupedOptions.map((group) => (
                        <div key={group.name || 'ungrouped'}>
                          {group.name ? (
                            <div className='px-2 pt-2 pb-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground'>
                              {group.name}
                            </div>
                          ) : null}
                          {group.items.map((option) =>
                            renderOptionRow(
                              option,
                              selectedValues.some((v) => v.value === option.value),
                            ),
                          )}
                        </div>
                      ))
                    : (
                      <>
                        {selectedOptions.map((option) => renderOptionRow(option, true))}
                        {unselectedOptions.map((option) => renderOptionRow(option, false))}
                      </>
                    )}

                  <div className='h-2' />
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {helperText && <p className='text-sm text-error'>{helperText}</p>}
    </div>
  );
}
